/**
 * One error pattern for both apps (DESIGN.md §5): localized text for failed loads (ErrorState),
 * failed actions (error toast) and failed sign-ins. Works on any error with the shape of
 * `@platform/api-client`'s ApiError (`status`, `code`, `message`); other errors get the
 * generic text.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import { useMemo } from 'react';
import { toast } from '../components/sonner';

const MAIL_CRYPTO_ERRORS: Record<string, MessageKey> = {
  mail_crypto_key_unavailable: 'mailCrypto.keyUnavailable', mail_crypto_disabled: 'mailCrypto.disabled',
  mail_crypto_changed: 'mailCrypto.changed', mail_crypto_material_invalid: 'mailCrypto.materialInvalid',
  mail_crypto_message_invalid: 'mailCrypto.messageInvalid', mail_crypto_identity_changed: 'mailCrypto.identityChanged',
  mail_crypto_mailbox_required: 'mailCrypto.identityChanged', mail_crypto_sender_mismatch: 'mailCrypto.senderMismatch',
  mail_crypto_fingerprint_mismatch: 'mailCrypto.fingerprintMismatch', mail_crypto_source_unavailable: 'mailCrypto.sourceUnavailable',
  mail_crypto_smime_bcc_unsupported: 'mailCrypto.smimeBccUnsupported', mail_crypto_message_too_large: 'mailCrypto.tooLarge',
  mail_crypto_local_peer_forbidden: 'mailCrypto.localPeerForbidden', mail_crypto_unavailable: 'mailCrypto.unavailable',
  mail_crypto_busy: 'mailCrypto.busy', mail_issuer_unavailable: 'mailCrypto.unavailable', mail_issuer_expiring: 'mailCrypto.unavailable',
};

const DNS_ERRORS: Record<string, MessageKey> = {
  dns_tsig: 'admin.certificates.credential.tsigError',
  dns_refused: 'admin.certificates.credential.refusedError',
  dns_notauth: 'admin.certificates.credential.notauthError',
  dns_timeout: 'admin.certificates.credential.timeoutError',
  dns_connection: 'admin.certificates.credential.connectionError',
  dns_soa: 'admin.certificates.credential.soaError',
  dns_update: 'admin.certificates.credential.updateError',
  dns_record_changed: 'admin.certificates.credential.changedError',
};

interface ErrorLike {
  status: number;
  code?: string;
  message?: string;
}

function asErrorLike(error: unknown): ErrorLike | undefined {
  if (typeof error !== 'object' || error === null || typeof (error as { status?: unknown }).status !== 'number') return undefined;
  return error as ErrorLike;
}

/** Stable error code of an API error (`invalid_pack`, `revision_mismatch`, …), if any. */
export function errorCode(error: unknown): string | undefined {
  return asErrorLike(error)?.code;
}

/** Full error body of an API error (extra fields like `issues`), if any. */
export function errorBody<T = unknown>(error: unknown): T | undefined {
  return asErrorLike(error) ? ((error as { body?: T }).body ?? undefined) : undefined;
}

/**
 * Code of a batch call where some items failed (the server answers per item; the app turns that
 * into one error with the counts as body).
 */
export const PARTIAL_FAILURE = 'partial_failure';
export interface PartialFailure {
  failed: number;
  total: number;
}

export interface ErrorText {
  /** Description for an ErrorState or an error toast. */
  describe: (error: unknown) => string;
  /** Message for the login form. */
  login: (error: unknown) => string;
  /** Error toast for a failed action (persists until dismissed, DESIGN.md T8). */
  notify: (error: unknown, title?: string) => void;
}

export function useErrorText(): ErrorText {
  const { t } = useI18n();
  return useMemo(() => {
    const describe = (error: unknown): string => {
      const api = asErrorLike(error);
      if (!api) return t('common.errorDescription');
      if (api.code === 'dns_cleanup' || api.code === 'dns_timeout' || api.code === 'dns_connection') {
        const details = errorBody<{ error?: { details?: { name?: string; value?: string } } }>(error)?.error?.details;
        if (details?.name) return t(api.code === 'dns_cleanup' ? 'admin.certificates.credential.cleanupError' : 'admin.certificates.credential.timeoutCleanupError', { name: details.name, value: details.value ?? '' });
      }
      if (api.code && DNS_ERRORS[api.code]) return t(DNS_ERRORS[api.code]!);
      const mailCryptoMessage = api.code ? MAIL_CRYPTO_ERRORS[api.code] : undefined;
      if (mailCryptoMessage) return t(mailCryptoMessage);
      if (api.code === 'bootstrap_unavailable') return t('firstAdminBootstrap.unavailable');
      if (api.code === 'last_instance_admin') return t('firstAdminBootstrap.continuity');
      if (api.code === 'admin_safety_busy') return t('firstAdminBootstrap.busy');
      if (api.code === 'invalid_code') return t('mfa.errorInvalid');
      if (api.code === 'mfa_challenge_expired') return t('mfa.errorExpired');
      if (api.code === 'enrolment_restricted') return t('mfa.errorRestricted');
      if (api.code === 'mfa_locked') return t('mfa.errorLocked');
      if (api.code === 'mfa_last_factor') return t('mfa.errorLastFactor');
      if (api.code === 'step_up_required') return t('mfa.errorStepUp');
      if (api.code === 'mfa_origin_unavailable' || api.code === 'mfa_origin_mismatch') return t('mfa.errorOrigin');
      if (api.status === 0) return t('errors.network');
      if (api.status === 401) return t('auth.sessionExpired');
      if (api.code === 'not_admin') return t('auth.notAdmin');
      if (api.code === 'native_jwt_probe_failed') return t('auth.sessionCheckFailed');
      if (api.code === 'native_identity_unavailable') return t('errors.nativeIdentityUnavailable');
      if (api.code === 'user_group_changed') return t('errors.userGroupChanged');
      if (api.code === 'user_group_member_changed') return t('errors.userGroupMemberChanged');
      if (api.code === 'user_group_member_unavailable') return t('errors.userGroupMemberUnavailable');
      if (api.code === 'user_group_domain_unavailable') return t('errors.userGroupDomainUnavailable');
      if (api.code === 'user_group_name_taken') return t('errors.userGroupNameTaken');
      if (api.code === 'user_group_capacity') return t('errors.userGroupCapacity');
      if (api.code === 'group_ad_writeback_unavailable') return t('errors.groupAdWritebackUnavailable');
      if (api.code === 'source_busy') return t('errors.migrationSourceBusy');
      if (api.code === 'source_test_busy') return t('errors.migrationSourceTestBusy');
      if (api.code === 'source_changed') return t('errors.migrationSourceChanged');
      if (api.code === 'source_restore_failed') return t('errors.migrationSourceRecoveryRequired');
      if (api.code === 'send_as_denied') return t('errors.sendAsDenied');
      if (api.code === 'host_key_mismatch') return t('admin.backups.target.hostKeyMismatch');
      if (api.code === 'folder_exists') return t('errors.folderExists');
      if (api.code === 'public_folder_name_exists') return t('errors.publicFolderNameExists');
      if (api.code === 'public_folder_has_children') return t('errors.publicFolderHasChildren');
      if (api.code === 'protected_folder') return t('errors.protectedFolder');
      if (api.code === 'draft_image_unavailable') return t('errors.draftImageUnavailable');
      if (api.code === 'forward_unsupported') return t('errors.forwardUnsupported');
      if (api.code === 'event_attachment_not_saved') return t('errors.eventAttachmentNotSaved');
      if (api.code === 'oof_schedule_unsupported') return t('errors.oofScheduleUnsupported');
      if (api.code === 'invalid_history_cursor') return t('common.quarantineHistory.invalidCursor');

      if (api.code === 'profile_owned_by_directory') return t('errors.profileOwnedByDirectory');
      if (api.code === 'profile_origin_unknown') return t('errors.profileOriginUnknown');
      if (api.code === 'dnsbl_credential_required') return t('errors.dnsblCredentialRequired');
      if (api.code === 'dnsbl_credential_invalid') return t('errors.dnsblCredentialInvalid');
      if (api.code === 'dnsbl_source_unavailable') return t('errors.dnsblSourceUnavailable');
      if (api.code === 'dnsbl_test_unavailable') return t('errors.dnsblTestUnavailable');
      if (api.code === 'source_in_use') return t('errors.sourceInUse');
      if (api.code === 'directory_discovery_stale') return t('errors.directoryDiscoveryStale');
      if (api.code === 'directory_delegation_required' || api.code === 'directory_egress_denied' || api.code === 'directory_scope_denied' || api.code === 'directory_shared_connection') return t('errors.directoryDelegationRequired');
      if (api.code === 'directory_review_required' || api.code === 'directory_selection_stale' || api.code === 'directory_group_stale') return t('errors.directorySelectionStale');
      if (api.code === 'directory_connection_in_use' || api.code === 'directory_explicit_rebind_required') return t('errors.directoryConnectionInUse');
      if (api.code === 'directory_connection_retired' || api.code === 'native_org_ldap_disabled') return t('errors.directoryConnectionRetired');
      if (api.code === 'directory_identity_conflict') return t('errors.directoryIdentityConflict');
      if (api.code === 'directory_identity_unverified') return t('errors.directoryIdentityUnverified');
      if (api.code === 'directory_context_conflict') return t('errors.directoryContextConflict');
      if (api.code === 'directory_discovery_unavailable' || api.code === 'directory_identity_unavailable') return t('errors.directoryDiscoveryUnavailable');
      if (api.code === 'directory_discovery_busy') return t('errors.directoryDiscoveryBusy');
      if (api.code === 'outside_connection_base') return t('errors.directoryScopeOutside');
      if (api.code === 'source_usage_unavailable') return t('errors.sourceUsageUnavailable');
      if (api.code === 'dnsbl_source_required') return t('errors.dnsblSourceRequired');
      if (api.code === 'dlp_source_required') return t('errors.dlpSourceRequired');
      if (api.code === 'reputation_provider_required') return t('errors.reputationProviderRequired');
      // Writes fail closed while the audit log cannot record them (platform API, plan logging-audit-trace.md §8).
      if (api.code === 'audit_unavailable') return t('errors.auditUnavailable');
      if (api.code === PARTIAL_FAILURE) {
        const counts = errorBody<PartialFailure>(error);
        if (counts) return t('errors.partialFailure', { failed: counts.failed, total: counts.total });
      }
      if (api.status === 503) return t('auth.serviceUnavailable');
      if (api.status === 403) return t('errors.forbidden');
      // Upstream details (e.g. "grommunio rejected the request: …") help the admin; English only.
      return api.message || t('common.errorDescription');
    };
    const login = (error: unknown): string => {
      const api = asErrorLike(error);
      if (api?.code === 'mfa_required') return t('mfa.challenge');
      if (api?.code === 'bootstrap_unavailable') return t('firstAdminBootstrap.unavailable');
      if (!api) return t('auth.invalidCredentials');
      if (api.status === 0) return t('auth.serverUnreachable');
      if (api.status === 429) return t('auth.rateLimited');
      if (api.code === 'native_identity_unavailable') return t('errors.nativeIdentityUnavailable');
      if (api.code === 'not_admin') return t('auth.notAdmin');
      if (api.status === 403) return t('auth.appDenied');
      if (api.code === 'native_jwt_probe_failed') return t('auth.sessionCheckFailed');
      if (api.status === 503) return t('auth.serviceUnavailable');
      if (api.status === 401 || api.status === 400) return t('auth.invalidCredentials');
      return t('auth.serverUnreachable');
    };
    const notify = (error: unknown, title?: string) => {
      // SessionProvider owns the terminal expiry notice. Retrying an action cannot restore it.
      if (asErrorLike(error)?.status === 401) return;
      toast.error(title ?? t('errors.actionFailed'), { description: describe(error), duration: Infinity, closeButton: true });
    };
    return { describe, login, notify };
  }, [t]);
}
